import * as React from 'react';
// router
import { Outlet } from 'react-router';
// mui
import {
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Snackbar,
  Alert,
} from '@mui/material';
import LayoutMenu from '../components/Layout/LayoutMenu';
// icons
import MenuIcon from '@mui/icons-material/Menu';
// redux
import { RootState } from '../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { setToken } from '../store/slices/authSlice';
import { clearError } from '../store/slices/apiErrorSlice';

const drawerWidth = 240;

export default function AppLayout() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const authToken = useSelector((state: RootState) => state.auth.token);
  const socketConnected = useSelector((state: RootState) => state.auth.socketConnected);
  const socketInitialized = useSelector((state: RootState) => state.auth.socketInitialized);
  const reconnectionCountdown = useSelector((state: RootState) => state.auth.reconnectionCountdown);
  const apiError = useSelector((state: RootState) => state.apiErrors.error);

  const [ mobileOpen, setMobileOpen ] = React.useState(false);
  const [ isClosing, setIsClosing ] = React.useState(false);

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  React.useEffect(() => {
    if (authToken && !socketConnected) {
      dispatch(setToken(authToken)); // force refresh of socket with token
    }
  }, []);

  React.useEffect(() => {
    if (!!apiError) {
      setTimeout(() => {
        dispatch(clearError());
      }, 1000*10);
    }
  }, [apiError]);

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          <AppBar position="static">
            <Toolbar>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2, display: { sm: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                Task Manager
              </Typography>
            </Toolbar>
          </AppBar>
        </Box>
      </AppBar>
      <Box
        component="nav"
        sx={{
          width: { sm: drawerWidth },
          flexShrink: { sm: 0 },
        }}
      >
        { isMobile ?
          // mobile drawer
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onTransitionEnd={handleDrawerTransitionEnd}
            onClose={handleDrawerClose}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
            slotProps={{
              root: {
                keepMounted: true,
              },
            }}
          >
            <Box
              sx={{
                background: isMobile ? theme.palette.background.default : '',
                height: '100%'
              }}
            >
              <Toolbar />
              <LayoutMenu
                onClickCallback={handleDrawerClose}
              />
            </Box>
          </Drawer>
        :
          // desktop drawer
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
            open
          >
            <Box
              sx={{
                height: '100%'
              }}
            >
              <Toolbar />
              <LayoutMenu />
            </Box>
          </Drawer>
        }
      </Box>

      <Box component="main" sx={{ flexGrow: 1, p: isMobile ? 0 : 3 }}>
        <Toolbar />

        <Outlet />
        
        <Snackbar
          open={!!apiError}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center'
          }}
        >
          <Alert severity="error" sx={{ width: '100%' }}>
            {apiError}
          </Alert>
        </Snackbar>

        <Snackbar
          open={socketInitialized && !socketConnected}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
        >
          <Alert severity="error" sx={{ width: '100%' }}>{
            reconnectionCountdown === 0
              ? 'Reconnecting...'
              : `Disconnected (Retrying in ${reconnectionCountdown})`
          }</Alert>
        </Snackbar>
      </Box>
    </Box>
  )
}
